import { useSyncExternalStore } from 'react';

import type { Value } from '@spotify-confidence/sdk';

import { createSessionStore, isStruct } from '../../utils';
import type { FlagValueInterceptor } from '../flagInterceptors';

export const FLAG_OVERRIDES_KEY = 'devbar-flag-overrides';

export type Overrides = Record<string, unknown>;

export const EMPTY_OVERRIDES: Overrides = Object.freeze({});

const parseOverrides = (raw: string): Overrides => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStruct(parsed) && Object.keys(parsed).length > 0
      ? parsed
      : EMPTY_OVERRIDES;
  } catch {
    return EMPTY_OVERRIDES;
  }
};

const overrideStore = createSessionStore<Overrides>({
  key: FLAG_OVERRIDES_KEY,
  empty: EMPTY_OVERRIDES,
  parse: parseOverrides,
  serialize: overrides => JSON.stringify(overrides),
});

export const useOverrides = (): Overrides =>
  useSyncExternalStore(
    overrideStore.subscribe,
    overrideStore.getSnapshot,
    overrideStore.getServerSnapshot,
  );

export const readOverrides = (): Overrides => overrideStore.read();

export const writeOverrides = (overrides: Overrides): void =>
  overrideStore.write(overrides);

export const clearOverrides = (): void => overrideStore.clear();

const readPath = (value: unknown, steps: string[]): unknown => {
  let current = value;
  for (const step of steps) {
    if (!isStruct(current)) {
      return undefined;
    }
    current = current[step];
  }
  return current;
};

const mergeOverride = <T>(resolvedValue: T, override: unknown): T => {
  if (
    typeof resolvedValue === 'object' &&
    resolvedValue !== null &&
    typeof override === 'object' &&
    override !== null
  ) {
    return { ...resolvedValue, ...override } as T;
  }
  return override as T;
};

export const applyOverride = <T>(
  flag: string,
  resolvedValue: T,
  overrides: Overrides,
): T => {
  if (flag in overrides) {
    return mergeOverride(resolvedValue, overrides[flag]);
  }

  const [base, ...steps] = flag.split('.');
  if (steps.length === 0 || !(base in overrides)) {
    return resolvedValue;
  }

  const override = readPath(overrides[base], steps);
  return override === undefined
    ? resolvedValue
    : mergeOverride(resolvedValue, override);
};

export const useLocalOverride = <T extends Value>(
  flag: string,
  resolvedValue: T,
): T => {
  const overrides = useOverrides();
  if (overrides === EMPTY_OVERRIDES) {
    return resolvedValue;
  }
  return applyOverride(flag, resolvedValue, overrides);
};

export const useLocalOverrideInterceptor: FlagValueInterceptor = <
  T extends Value,
>(
  flag: string,
  _defaultValue: T,
  resolvedValue: T,
): T => useLocalOverride(flag, resolvedValue);
